import React, { FC, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { Campaign } from 'models/restTypes';
import { useStores } from 'stores/hooks/hooks';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const CampaignsTable: FC<{
  campaigns: Campaign[];
  isLoading: boolean;
}> = observer(({ campaigns, isLoading }) => {
  const { campaignsStore } = useStores();
  const { t } = useTranslation('campaigns');

  const [filters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    uuid: { value: '', matchMode: FilterMatchMode.CONTAINS },
    externalUuid: { value: '', matchMode: FilterMatchMode.CONTAINS },
    name: { value: '', matchMode: FilterMatchMode.CONTAINS },
    b2bAccountEmail: { value: '', matchMode: FilterMatchMode.CONTAINS },
    description: { value: '', matchMode: FilterMatchMode.CONTAINS },
    status: { value: '', matchMode: FilterMatchMode.CONTAINS },
    agentId: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(
    ({ data }: DataTableRowClickEvent) => {
      campaignsStore.setSelectedCampaign(data as Campaign);
      console.log('Selected campaign:', data);
    },
    [campaignsStore],
  );

  const messageTextBody = useCallback((rowData: Campaign) => {
    if (!rowData.messageText) return '-';
    return rowData.messageText.length > 50
      ? `${rowData.messageText.substring(0, 50)}...`
      : rowData.messageText;
  }, []);

  const imageUriBody = useCallback((rowData: Campaign) => {
    if (!rowData.imageUri) return '-';
    return (
      <a
        href={rowData.imageUri}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={rowData.imageUri}
        title={rowData.imageUri}
      >
        {rowData.imageUri}
      </a>
    );
  }, []);

  const areasBody = useCallback((rowData: Campaign) => {
    return rowData.areas.length > 0 ? rowData.areas.join(', ') : '-';
  }, []);

  const countryCodesBody = useCallback((rowData: Campaign) => {
    return rowData.countryCodes.length > 0
      ? rowData.countryCodes.join(', ')
      : '-';
  }, []);

  const cropTypeIdsBody = useCallback((rowData: Campaign) => {
    return rowData.cropTypeIds.length > 0
      ? rowData.cropTypeIds.join(', ')
      : '-';
  }, []);

  const notificationTypeIdsBody = useCallback((rowData: Campaign) => {
    return rowData.notificationTypeIds.length > 0
      ? rowData.notificationTypeIds.join(', ')
      : '-';
  }, []);

  const externalUuidBody = useCallback((rowData: Campaign) => {
    return rowData.externalUuid || '-';
  }, []);

  const b2bAccountEmailBody = useCallback((rowData: Campaign) => {
    return rowData.b2bAccountEmail || '-';
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledGrid flex={1}>
      <DataTableContainer elevation={3}>
        <DataTable
          id="CampaignsTable"
          showGridlines
          resizableColumns
          value={campaigns}
          selectionMode="single"
          scrollable
          scrollHeight="flex"
          emptyMessage={t('empty_message', { ns: 'staticData' })}
          stripedRows
          filters={filters}
          onRowClick={onRowClick}
          selection={campaignsStore.selectedCampaign}
          filterDisplay="row"
          globalFilterFields={[
            'uuid',
            'externalUuid',
            'name',
            'b2bAccountEmail',
            'description',
            'status',
            'agentId',
          ]}
          paginator
          rows={10}
        >
          <Column field="uuid" header={t('uuid')} sortable filter />
          <Column
            field="externalUuid"
            header={t('external_uuid')}
            sortable
            filter
            body={externalUuidBody}
          />
          <Column field="name" header={t('name')} sortable filter />
          <Column
            field="b2bAccountEmail"
            header={t('b2b_account_email')}
            sortable
            filter
            body={b2bAccountEmailBody}
          />
          <Column
            field="description"
            header={t('description')}
            sortable
            filter
          />
          <Column field="status" header={t('status')} sortable filter />
          <Column
            field="messageText"
            header={t('message_text')}
            sortable
            body={messageTextBody}
            style={{ maxWidth: '300px' }}
          />
          <Column
            field="imageUri"
            header={t('image_uri')}
            sortable
            body={imageUriBody}
          />
          <Column field="agentId" header={t('agent_id')} sortable filter />
          <Column field="areas" header={t('areas')} sortable body={areasBody} />
          <Column
            field="cropTypeIds"
            header={t('crop_type_ids')}
            sortable
            body={cropTypeIdsBody}
          />
          <Column
            field="countryCodes"
            header={t('country_codes')}
            sortable
            body={countryCodesBody}
          />
          <Column
            field="notificationTypeIds"
            header={t('create_dialog_notification_type_ids')}
            sortable
            body={notificationTypeIdsBody}
          />
        </DataTable>
      </DataTableContainer>
    </StyledGrid>
  );
});

export default CampaignsTable;
